/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/prop-types */
import {
  CheckCircleIcon as CheckCircleIconOutline,
  StarIcon as StarIconOutline,
  XCircleIcon as XCircleIconOutline,
} from "@heroicons/react/24/outline";
import { Badge, Menu } from "@mantine/core";
import { ICellTextProps } from "ka-table/props";
import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { IconTrafficLights } from "@tabler/icons-react";
import { queryClient } from "../../hooks/QueryHooks";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import { updateSignalRun } from "../../services/inbox/inboxEndpoints";
import notify from "../../utils/notify";

/**
 * Custom cell component for signal status with dropdown menu to update status.
 */
const SignalStatusCell: React.FC<ICellTextProps> = React.memo(
  ({
    column, dispatch, rowKeyValue, ...props
  }) => {
    const workspace = useWorkspaceStore((state) => state.workspace);
    const originalValue = props.rowData[`${column.key}`];
    // Use local state to track the current status value
    const [currentStatus, setCurrentStatus] = useState(originalValue);
    const isNullOrEmpty = currentStatus === null || currentStatus === undefined || currentStatus === "";
    const signalId = props.rowData?.["sr.id"];
    
    // Update local state when props change
    useEffect(() => {
      setCurrentStatus(originalValue);
    }, [originalValue]);
    
    const statusMutation = useMutation({
      mutationFn: updateSignalRun,
    });

    const onSelect = (stat: string) => {
      if (signalId) {
        // Optimistically update UI
        setCurrentStatus(stat);
        
        statusMutation.mutate({
          signal_run_id: signalId,
          workspace_id: workspace.id,
          status: stat,
          automation_id: props.rowData?.["sr.automation_id"],
        }, {
          onSuccess: () => {
            // Invalidate queries to refresh data from backend
            queryClient.invalidateQueries(["inbox"]);
            queryClient.invalidateQueries(["signals"]);
            
            notify({
              messageList: [`Signal status set to ${stat}`],
              type: "info",
            });
          },
          onError: () => {
            // Revert to original value if there's an error
            setCurrentStatus(originalValue);
            notify({
              messageList: ["Failed to update signal status"],
              type: "failure",
            });
          }
        });
      }
    };

    // Determine the status badge properties
    const getStatusProperties = () => {
      if (isNullOrEmpty) {
        return {
          icon: <IconTrafficLights width={13} />,
          text: "Set Status",
          color: "gray",
          variant: "outline"
        };
      }
      
      switch (currentStatus) {
        case "starred":
          return {
            text: "Important",
            color: "yellow.6",
            variant: "dot"
          };
        case "done":
          return {
            text: "Done",
            color: "green.6",
            variant: "dot"
          };
        case "dismissed":
          return {
            text: "Dismissed",
            color: "red.6",
            variant: "dot"
          };
        default:
          return {
            text: "Unknown",
            color: "blue.6",
            variant: "dot",
          };
      }
    };
    
    const statusProps = getStatusProperties();

    return (
      <Menu shadow="md" width={180} position="bottom-end">
        <Menu.Target>
            <Badge
              radius="sm"
              leftSection={statusProps.icon} 
              color={statusProps.color} 
              variant={statusProps.variant}
              className="hover:opacity-90 !cursor-pointer transition-opacity !lowercase !capitalize relative"
            >
              {statusProps.text}
            </Badge>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Status</Menu.Label>
          <Menu.Item 
            className={currentStatus === "starred" ? "bg-gray-800 text-white" : ""} 
            onClick={() => { onSelect("starred"); }} 
            leftSection={<StarIconOutline width={20} />}
          >
            Star
          </Menu.Item>
          <Menu.Item 
            className={currentStatus === "done" ? "bg-gray-800 text-white" : ""} 
            onClick={() => { onSelect("done"); }} 
            leftSection={<CheckCircleIconOutline width={20} />}
          >
            Done
          </Menu.Item>
          <Menu.Item 
            className={currentStatus === "dismissed" ? "bg-gray-800 text-white" : ""} 
            onClick={() => { onSelect("dismissed"); }} 
            leftSection={<XCircleIconOutline width={20} />}
          >
            Dismissed
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  },
);

export default SignalStatusCell;
